import React, { useContext, useEffect, useMemo, useState } from "react";
import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import FroalaEditor from "react-froala-wysiwyg";
import Froalaeditor from "froala-editor";
import { t } from "i18next";
import ModalMain from "../../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../../components/Modal/ModalFooter";
import themeContext from "../../../../../context/theme/ThemeContext";
import { getEnv } from "../../../../../helpers/EnvHelper";
import "../../../styles/styles.css";

const getFinalHtml = (tab) => {
  if (!tab?.reportDetails) return "";
  const d = tab.reportDetails;
  if (d.report != null && d.report !== "") return d.report;
  return d.finalReport || "";
};

const ReportSnapshotRecoveryModal = ({
  show,
  draftTabs,
  officialTabs,
  isOfficialPreliminary = false,
  onAccept,
  onClose,
  onReject,
}) => {
  const { getThemeColor, themeName } = useContext(themeContext);
  const isVerticalMonitor = window.innerWidth <= 1200;
  const formatterEditorHeight = isVerticalMonitor
    ? Math.min(Math.max(window.innerHeight - 350, 200), 350)
    : Math.min(Math.max(window.innerHeight - 350, 300), 500);
  const draftTabsSafe = draftTabs ?? [];
  const officialTabsSafe = officialTabs ?? [];
  const [activeTemplateIdx, setActiveTemplateIdx] = useState(0);
  const [isDraftView, setIsDraftView] = useState(true);
  const [editorLoading, setEditorLoading] = useState(false);

  const officialLabel = isOfficialPreliminary
    ? t("button.preliminary-report")
    : t("button.current-report");
  const compareDescription = isOfficialPreliminary
    ? t("string-literals.compare-draft-with-preliminary")
    : t("string-literals.compare-draft-with-current");

  useEffect(() => {
    setEditorLoading(true);
    const timer = window.setTimeout(() => {
      setEditorLoading(false);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [activeTemplateIdx, isDraftView]);

  const handleTabChange = (idx) => {
    if (idx === activeTemplateIdx) return;
    setEditorLoading(true);
    setActiveTemplateIdx(idx);
  };

  const handleToggleView = (isDraft) => {
    if (isDraft === isDraftView) return;
    setEditorLoading(true);
    setIsDraftView(isDraft);
  };

  const currentTabs = useMemo(
    () => (isDraftView ? draftTabsSafe : officialTabsSafe),
    [isDraftView, draftTabsSafe, officialTabsSafe]
  );

  useEffect(() => {
    if (activeTemplateIdx >= currentTabs.length) {
      setActiveTemplateIdx(0);
    }
  }, [activeTemplateIdx, currentTabs]);

  const activeTab = currentTabs[activeTemplateIdx] ?? currentTabs[0];
  const editorModel = getFinalHtml(activeTab);

  const getReadonlyConfig = () => ({
    key: getEnv("REACT_APP_FROALA_API_KEY"),
    attribution: false,
    charCounterCount: false,
    wordCounterCount: false,
    height: formatterEditorHeight,
    width: 818,
    toolbarButtons: {
      moreText: {
        buttons: [],
        align: "left",
      },
    },
    events: {
      initialized: function () {
        this.edit.off();
      },
    },
  });

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("string-literals.draft-available")}
        handleCancel={onClose}
      />
    ),
    body: (
      <div style={{ padding: "8px 12px", color: getThemeColor("primary-color") }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            margin: "4px 0 8px",
            flexWrap: "wrap",
          }}
        >
          <p style={{ margin: 0, fontSize: 14, flex: "1 1 340px" }}>
            {compareDescription}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", flex: "0 0 auto" }}>
            <PrimaryButton
              onClick={() => handleToggleView(true)}
              text={t("button.draft")}
              style={{
                borderRadius: "20px 0 0 20px",
                background: isDraftView ? "" : "black",
                color: "white",
              }}
            />
            <PrimaryButton
              onClick={() => handleToggleView(false)}
              text={officialLabel}
              style={{
                borderRadius: "0 20px 20px 0",
                background: isDraftView ? "black" : "",
                color: "white",
              }}
            />
          </div>
        </div>
        <div className="table-report">
          <ul
            id="horizontal-list"
            style={{
              minWidth: "unset",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              overflowX: "hidden",
              overflowY: "auto",
              whiteSpace: "normal",
              rowGap: "6px",
            }}
          >
            {currentTabs.map((tab, idx) => (
              <li
                key={tab.key ?? `tab-${idx}`}
                onClick={() => handleTabChange(idx)}
                className={
                  idx === activeTemplateIdx
                    ? `${themeName}-active-li li-tab`
                    : "li-tab"
                }
                style={{
                  background: getThemeColor("report-not-active-tab"),
                  color: getThemeColor("primary-color"),
                  flex: "0 0 auto",
                }}
              >
                <span>{tab.text ?? `Report ${idx + 1}`}</span>
              </li>
            ))}
          </ul>
        </div>
        <div
          style={{
            marginTop: "0px",
            minHeight: `${formatterEditorHeight + 18}px`,
            height: `${formatterEditorHeight + 18}px`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: `${formatterEditorHeight}px`,
              opacity: editorLoading ? 0.25 : 1,
              transition: "opacity 120ms ease",
            }}
          >
            <FroalaEditor
              tag="textarea"
              config={getReadonlyConfig()}
              model={editorModel}
              key={`${activeTemplateIdx}-${isDraftView ? "draft" : "official"}`}
            />
          </div>
          {editorLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.04)",
                zIndex: 3,
              }}
            >
              <Spinner size={SpinnerSize.large} />
            </div>
          )}
        </div>
      </div>
    ),
    footer: (
      <ModalFooter
        PrimaryButtonList={[
          {
            title: t("button.restore-draft"),
            onclick: onAccept,
          },
        ]}
        DefaultButtonList={[
          {
            title: t("button.discard-draft"),
            onclick: onReject,
          },
        ]}
      />
    ),
    width: "860px",
    maxWidth: "calc(100vw - 40px)",
    maxHeight: "100%",
  };

  if (!show) return null;

  return (
    <ModalMain
      updatedModal={true}
      modalDetails={modalDetails}
      showModal={show}
    />
  );
};

export default ReportSnapshotRecoveryModal;
